/**
 * The typed reveal (R27) — one line's text, complete in the DOM from the
 * first frame, visually revealed at its channel's cadence.
 *
 * @remarks
 * Calder's mask constraint (D99, non-negotiable): **never insert characters.**
 * The transcript is a `role="log"` live region, so every DOM mutation inside
 * it is an announcement — character insertion is forty announcements per
 * sentence to the users least able to work around it, a truncated copy-paste
 * (D86's premise broken by construction), and an export missing its last
 * paragraph. This component renders the COMPLETE text immediately and
 * animates only a visual reveal over it: per-character opacity for the
 * stepped cadences, a focus-in for perception. Selection, copy, the export,
 * and the accessibility tree all see the finished line from frame one —
 * opacity hides from the eye, never from the clipboard.
 *
 * Cadence is characterization (Calder's table; Mike's metronome ruling), so
 * WHICH cadence a channel gets is the caller's statement about who produces
 * the text — this component only performs it. D102 moved the PACE here from
 * the stylesheet: lines in one turn animate IN ORDER (Mike's finding — three
 * of her lines landing at once read as a wall, not a sequence), and ordering
 * requires knowing each line's duration, so the durations and the delays are
 * computed in one place and handed to the CSS as variables. All of it is
 * presentation-local in the D85 sense: read-only, never saved, invisible to
 * `(bundle, seed, commands)`.
 */

import { RedactionComponent } from './redaction-component.js';
import { revealStepsOf, segmentRedactions } from './redaction.js';
import { resolveReveal, type RevealCadence } from './reveal-cadence.js';

// D137: the cadence type, the table, the limits, and `revealDurationOf` live
// in the framework-free `reveal-cadence.ts` so a page with no React (the
// 404) reads the same table. Re-exported so this module's callers are unmoved.
export { FOCUS_REVEAL_DURATION_MS, type RevealCadence, revealDurationOf } from './reveal-cadence.js';

/** Props for {@link RevealedTextComponent}. */
export interface RevealedTextComponentProps {
	/** The reveal to perform over the (already complete) text. */
	readonly cadence: RevealCadence;
	/**
	 * D102: when this line's reveal begins, measured from the turn's start —
	 * the sum of every earlier arriving line's duration (plus breaths), so the
	 * turn's lines land in ORDER rather than all at once. Omitted = 0.
	 */
	readonly startDelayMs?: number;
	/** Per-character pace for the stepped cadence, in milliseconds. */
	readonly stepMs?: number;
	/** The full line, verbatim — rendered whole whatever the cadence. */
	readonly text: string;
}

/**
 * Render one line's complete text with its channel's visual reveal.
 *
 * @param props - {@link RevealedTextComponentProps}
 * @returns The revealed text
 */
export const RevealedTextComponent = ({ cadence, startDelayMs, stepMs, text }: RevealedTextComponentProps) => {
	const reveal = resolveReveal(cadence, text, stepMs);

	if (reveal.kind === 'instant') {
		return <RedactedProse text={text} />;
	}

	const lineDelayStyle = { ['--reveal-line-delay' as string]: `${String(startDelayMs ?? 0)}ms` };

	if (reveal.kind === 'focus') {
		return (
			<span className="revealed-text revealed-text--focus" style={lineDelayStyle}>
				<RedactedProse text={text} />
			</span>
		);
	}

	return (
		<span
			className="revealed-text revealed-text--stepped"
			style={{ ...lineDelayStyle, ['--reveal-step' as string]: `${String(reveal.stepMs)}ms` }}
		>
			{/*
			 * One step per code point — not per UTF-16 unit, so a surrogate pair
			 * (the ▷ family's future relatives) is one step, never two half-glyphs
			 * — and one step per redaction (D145): a bar goes on the display in
			 * one motion. The index key is CORRECT here, not a compromise: the
			 * list is derived from an immutable string and never reorders.
			 */}
			{revealStepsOf(text).map((step, stepIndex) => (
				<span
					className="revealed-text__step"
					// eslint-disable-next-line react/no-array-index-key -- derived from an immutable string; positions never reorder
					key={stepIndex}
					style={{ ['--reveal-step-index' as string]: stepIndex }}
				>
					{step.kind === 'redaction' ? <RedactionComponent /> : step.text}
				</span>
			))}
		</span>
	);
};

/**
 * A line's prose with each `[REDACTED]` drawn as the bar (D145) — the
 * whole-line renderers' half of what the stepped reveal does per step.
 *
 * @param props - The line as the transcript stores it
 * @returns The prose runs and bars, in order
 */
const RedactedProse = ({ text }: { readonly text: string }) => (
	<>
		{segmentRedactions(text).map((segment, segmentIndex) =>
			segment.kind === 'redaction' ? (
				// eslint-disable-next-line react/no-array-index-key -- derived from an immutable string; positions never reorder
				<RedactionComponent key={segmentIndex} />
			) : (
				segment.text
			),
		)}
	</>
);
